/**
 * Tax Forms Page
 * Generate Schedule C and review tax form data ready for filing or CPA handoff
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  FileText,
  Printer,
  Building2,
  AlertCircle,
} from 'lucide-react';

interface BusinessProfile {
  id: string;
  name: string;
  taxId: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

interface ScheduleCData {
  businessProfile: BusinessProfile | null;
  grossReceipts: number;
  taxCollected: number;
  expenseLines: { taxCategory: string; totalAmount: number; itemCount: number }[];
  totalExpenses: number;
  netProfit: number;
}

// Schedule C line number mapping (IRS category -> line)
const SCHEDULE_C_LINES: Record<string, { line: string; description: string }> = {
  'Advertising': { line: '8', description: 'Advertising' },
  'Car & Truck Expenses': { line: '9', description: 'Car and truck expenses' },
  'Commissions & Fees': { line: '10', description: 'Commissions and fees' },
  'Contract Labor': { line: '11', description: 'Contract labor' },
  'Depreciation': { line: '13', description: 'Depreciation and section 179' },
  'Employee Benefits': { line: '14', description: 'Employee benefit programs' },
  'Insurance (non-health)': { line: '15', description: 'Insurance (other than health)' },
  'Interest (Mortgage)': { line: '16a', description: 'Interest: Mortgage' },
  'Interest (Other)': { line: '16b', description: 'Interest: Other' },
  'Legal & Professional Services': { line: '17', description: 'Legal and professional services' },
  'Office Expense': { line: '18', description: 'Office expense' },
  'Pension & Profit-Sharing': { line: '19', description: 'Pension and profit-sharing plans' },
  'Rent or Lease (Vehicles/Equipment)': { line: '20a', description: 'Rent: Vehicles, machinery, equipment' },
  'Rent or Lease (Property)': { line: '20b', description: 'Rent: Other business property' },
  'Repairs & Maintenance': { line: '21', description: 'Repairs and maintenance' },
  'Supplies': { line: '22', description: 'Supplies' },
  'Taxes & Licenses': { line: '23', description: 'Taxes and licenses' },
  'Travel': { line: '24a', description: 'Travel' },
  'Meals (50%)': { line: '24b', description: 'Deductible meals' },
  'Utilities': { line: '25', description: 'Utilities' },
  'Wages': { line: '26', description: 'Wages' },
  'Other Expenses': { line: '27', description: 'Other expenses' },
};

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

export function TaxFormsPage() {
  const [taxYear, setTaxYear] = useState(currentYear);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');

  const { data: profilesData } = useQuery({
    queryKey: ['business-profiles'],
    queryFn: () => api.getBusinessProfiles(),
  });
  const profiles = (profilesData?.data || []) as BusinessProfile[];

  // Auto-select first profile
  if (!selectedProfileId && profiles.length > 0) {
    setSelectedProfileId(profiles[0].id);
  }

  const { data: scheduleCData, isLoading } = useQuery({
    queryKey: ['schedule-c', taxYear, selectedProfileId],
    queryFn: async () => {
      const result = await api.getScheduleC({ taxYear, businessProfileId: selectedProfileId });
      return result.data as ScheduleCData;
    },
    enabled: !!selectedProfileId,
  });

  const handlePrint = () => {
    window.print();
  };

  // Map expense lines to Schedule C format
  const scheduleCLines = (scheduleCData?.expenseLines || []).map(line => ({
    ...line,
    lineInfo: SCHEDULE_C_LINES[line.taxCategory] || { line: '27', description: line.taxCategory },
  })).sort((a, b) => {
    const aNum = parseFloat(a.lineInfo.line.replace(/[a-z]/g, '.5')) || 99;
    const bNum = parseFloat(b.lineInfo.line.replace(/[a-z]/g, '.5')) || 99;
    return aNum - bNum;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tax Forms</h1>
          <p className="text-muted-foreground">Review and export tax form data for filing</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select Business" />
            </SelectTrigger>
            <SelectContent>
              {profiles.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={taxYear.toString()} onValueChange={(v) => setTaxYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(y => (
                <SelectItem key={y} value={y.toString()}>TY {y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!selectedProfileId ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-lg font-medium">Select a Business Profile</p>
            <p className="text-muted-foreground text-sm">Choose a business above to generate tax form data</p>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* Schedule C Form */}
          <Card className="print:shadow-none print:border-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Schedule C - Profit or Loss From Business
                  </CardTitle>
                  <CardDescription>
                    IRS Form 1040, Schedule C &middot; Tax Year {taxYear}
                  </CardDescription>
                </div>
                <div className="flex gap-2 print:hidden">
                  <Button variant="outline" size="sm" onClick={handlePrint}>
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Business Info */}
              <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-muted/50">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">Business Name</div>
                  <div className="font-medium">{scheduleCData?.businessProfile?.name || '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">EIN / Tax ID</div>
                  <div className="font-medium">{scheduleCData?.businessProfile?.taxId || 'Not set'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">City</div>
                  <div className="font-medium">{scheduleCData?.businessProfile?.city || '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">State / ZIP</div>
                  <div className="font-medium">
                    {scheduleCData?.businessProfile?.state || '-'} {scheduleCData?.businessProfile?.zip || ''}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Part I - Income */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Part I - Income</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 rounded bg-muted/30">
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary" className="font-mono w-12 justify-center">1</Badge>
                      <span>Gross receipts or sales</span>
                    </div>
                    <span className="font-bold text-lg">{formatCurrency(scheduleCData?.grossReceipts || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded bg-muted/30">
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary" className="font-mono w-12 justify-center">7</Badge>
                      <span>Gross income</span>
                    </div>
                    <span className="font-bold text-lg">{formatCurrency(scheduleCData?.grossReceipts || 0)}</span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Part II - Expenses */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Part II - Expenses</h3>
                {scheduleCLines.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No deductible expenses categorized for {taxYear}</p>
                    <p className="text-xs mt-1">Mark expenses as tax deductible and assign IRS categories in the Billing dashboard</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {scheduleCLines.map((line) => (
                      <div
                        key={line.taxCategory}
                        className="flex items-center justify-between p-3 rounded bg-muted/30"
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="secondary" className="font-mono w-12 justify-center">
                            {line.lineInfo.line}
                          </Badge>
                          <div>
                            <span>{line.lineInfo.description}</span>
                            <span className="text-xs text-muted-foreground ml-2">({line.itemCount} items)</span>
                          </div>
                        </div>
                        <span className="font-medium">{formatCurrency(line.totalAmount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Separator />

              {/* Totals */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary" className="font-mono w-12 justify-center">28</Badge>
                    <span className="font-semibold">Total expenses</span>
                  </div>
                  <span className="font-bold text-lg">{formatCurrency(scheduleCData?.totalExpenses || 0)}</span>
                </div>
                <div className="flex items-center justify-between p-4 rounded bg-primary/5 border-2 border-primary/20">
                  <div className="flex items-center gap-3">
                    <Badge className="font-mono w-12 justify-center">31</Badge>
                    <span className="font-bold text-lg">Net profit (or loss)</span>
                  </div>
                  <span className={`font-bold text-xl ${(scheduleCData?.netProfit || 0) >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>
                    {formatCurrency(scheduleCData?.netProfit || 0)}
                  </span>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">For Reference Only</p>
                    <p className="text-xs mt-1">
                      This is an aggregated summary based on your recorded expenses and revenue.
                      It is not an official IRS form. Consult your CPA or tax professional for actual filing.
                      Print or export this data to share with your accountant.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Forms Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Schedule SE - Self-Employment Tax</CardTitle>
                <CardDescription>Calculated from Schedule C net profit</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm">Net earnings from self-employment</span>
                    <span className="font-medium">{formatCurrency(scheduleCData?.netProfit || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">92.35% of net earnings</span>
                    <span className="font-medium">{formatCurrency((scheduleCData?.netProfit || 0) * 0.9235)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-sm font-semibold">Self-employment tax (15.3%)</span>
                    <span className="font-bold">{formatCurrency((scheduleCData?.netProfit || 0) * 0.9235 * 0.153)}</span>
                  </div>
                  <div className="flex justify-between text-emerald-600">
                    <span className="text-sm">Deductible half of SE tax</span>
                    <span className="font-medium">{formatCurrency((scheduleCData?.netProfit || 0) * 0.9235 * 0.153 / 2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">1099 Income Tracking</CardTitle>
                <CardDescription>Revenue from clients who should issue 1099-NEC</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-6 text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    Revenue tracked in your invoices can be cross-referenced with 1099-NEC forms received from clients.
                  </p>
                  <p className="text-xs mt-2">
                    Total gross receipts: <span className="font-medium">{formatCurrency(scheduleCData?.grossReceipts || 0)}</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
